import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Package, ArrowLeft, Edit, Trash2, PackagePlus, PackageMinus,
  RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Calendar,
  DollarSign, Hash, Layers, Box
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventoryItemDetail() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Adjust stock dialog
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    adjustment_type: "add",
    quantity: "",
    reason: ""
  });

  useEffect(() => {
    loadItem();
  }, [itemId]);

  const loadItem = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory/items/${itemId}`);
      setItem(response.data.item);
      setMovements(response.data.movements || []);
    } catch (error) {
      console.error("Failed to load item:", error);
      toast.error("Item not found");
      navigate("/dashboard/inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = async () => {
    if (!adjustForm.quantity || !adjustForm.reason) {
      toast.error("Quantity and reason are required");
      return;
    }

    try {
      await axios.post(`${API}/inventory/adjust-stock`, {
        item_id: itemId,
        adjustment_type: adjustForm.adjustment_type,
        quantity: parseFloat(adjustForm.quantity),
        reason: adjustForm.reason
      });
      toast.success("Stock adjusted");
      setShowAdjustDialog(false);
      setAdjustForm({ adjustment_type: "add", quantity: "", reason: "" });
      loadItem();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adjust stock");
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Archive "${item.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/inventory/items/${itemId}`);
      toast.success("Item archived");
      navigate("/dashboard/inventory");
    } catch (error) {
      toast.error("Failed to archive item");
    }
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case "StockIn":
        return <TrendingUp className="w-4 h-4 text-emerald-600" />;
      case "StockOut":
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <RefreshCw className="w-4 h-4 text-blue-600" />;
    }
  };

  const getMovementBadge = (type) => {
    switch (type) {
      case "StockIn":
        return <Badge className="bg-emerald-100 text-emerald-800">Stock In</Badge>;
      case "StockOut":
        return <Badge className="bg-red-100 text-red-800">Stock Out</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-800">Adjustment</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Item not found</p>
        <Button onClick={() => navigate("/dashboard/inventory")} className="mt-4">
          Back to Inventory
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-item-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/inventory")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Item Details Card */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
              <Package className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{item.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {item.classification && (
                  <Badge variant="outline">{item.classification.name}</Badge>
                )}
                {item.sku && (
                  <span className="text-sm text-gray-500">SKU: {item.sku}</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {item.track_quantity && (
              <Button variant="outline" onClick={() => setShowAdjustDialog(true)} data-testid="adjust-stock-btn">
                <PackagePlus className="w-4 h-4 mr-2" />
                Adjust Stock
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/dashboard/inventory?edit=${itemId}`)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" className="text-red-600" onClick={handleDelete}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Box className="w-4 h-4" />
              Current Stock
            </div>
            <p className="text-2xl font-bold mt-1">
              {item.track_quantity ? (
                <>
                  {item.current_quantity}
                  <span className="text-sm font-normal text-gray-500 ml-1">{item.unit}</span>
                </>
              ) : (
                <span className="text-gray-400">N/A</span>
              )}
            </p>
            {item.is_low_stock && (
              <div className="flex items-center gap-1 text-amber-600 text-xs mt-1">
                <AlertTriangle className="w-3 h-3" />
                Low stock
              </div>
            )}
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <DollarSign className="w-4 h-4" />
              Cost Price
            </div>
            <p className="text-2xl font-bold mt-1">
              {item.cost_price ? `$${item.cost_price.toFixed(2)}` : "-"}
            </p>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <DollarSign className="w-4 h-4" />
              Selling Price
            </div>
            <p className="text-2xl font-bold mt-1">
              {item.selling_price ? `$${item.selling_price.toFixed(2)}` : "-"}
            </p>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Hash className="w-4 h-4" />
              Reorder Level
            </div>
            <p className="text-2xl font-bold mt-1">
              {item.reorder_level !== null ? item.reorder_level : "-"}
            </p>
          </div>
        </div>

        {/* Stock Value */}
        {item.track_quantity && item.cost_price && (
          <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
            <p className="text-sm text-emerald-700">
              <strong>Stock Value:</strong> ${(item.current_quantity * item.cost_price).toFixed(2)}
            </p>
          </div>
        )}
      </div>

      {/* Movement History */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h2 className="font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            Stock Movement History
          </h2>
        </div>
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  No movements recorded yet
                </TableCell>
              </TableRow>
            ) : (
              movements.map((movement) => (
                <TableRow key={movement.id}>
                  <TableCell className="whitespace-nowrap">
                    {new Date(movement.movement_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getMovementIcon(movement.movement_type)}
                      {getMovementBadge(movement.movement_type)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <span className={
                      movement.movement_type === "StockIn" ? "text-emerald-600" :
                      movement.movement_type === "StockOut" ? "text-red-600" : ""
                    }>
                      {movement.movement_type === "StockIn" ? "+" : movement.movement_type === "StockOut" ? "-" : ""}
                      {movement.quantity}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {movement.reference_type}
                    </Badge>
                    {movement.reference_id && (
                      <span className="text-xs text-gray-500 ml-1">
                        #{movement.reference_id.slice(0, 8)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-500 text-sm max-w-xs truncate">
                    {movement.notes || "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Adjust Stock Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent data-testid="adjust-stock-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>
              Current stock: {item.current_quantity} {item.unit}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Adjustment Type</Label>
              <Select
                value={adjustForm.adjustment_type}
                onValueChange={(v) => setAdjustForm({ ...adjustForm, adjustment_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <span className="flex items-center gap-2">
                      <PackagePlus className="w-4 h-4 text-emerald-600" />
                      Add Stock
                    </span>
                  </SelectItem>
                  <SelectItem value="remove">
                    <span className="flex items-center gap-2">
                      <PackageMinus className="w-4 h-4 text-red-600" />
                      Remove Stock
                    </span>
                  </SelectItem>
                  <SelectItem value="set">
                    <span className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-blue-600" />
                      Set Exact Count
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={adjustForm.quantity}
                onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
                placeholder="Enter quantity"
              />
            </div>
            
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                placeholder="Why are you adjusting stock? (Required)"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdjustStock}>
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
